/**
 * Office hours. Response deadlines count only working time (not nights,
 * weekends, holidays or the lunch break), and IT alerts wait for the office
 * to open. Times are Ghana time, which is UTC all year (no daylight saving),
 * so the arithmetic below works in UTC.
 */
import { z } from 'zod';

export interface Holiday {
  /** YYYY-MM-DD */
  date: string;
  name: string;
}

export interface OfficeBreak {
  /** "12:00" */
  start: string;
  /** "13:00" */
  end: string;
}

export interface OfficeSchedule {
  /** Working weekdays: 0 = Sunday … 6 = Saturday. */
  days: number[];
  /** "08:00" */
  open: string;
  /** "17:00" */
  close: string;
  /** Daily breaks, e.g. lunch. Response deadlines don't count them. */
  breaks: OfficeBreak[];
  holidays: Holiday[];
}

export const DEFAULT_SCHEDULE: OfficeSchedule = {
  days: [1, 2, 3, 4, 5],
  open: '08:00',
  close: '17:00',
  breaks: [{ start: '12:00', end: '13:00' }],
  holidays: [],
};

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 08:00.');
export const officeScheduleSchema = z
  .object({
    days: z.array(z.number().int().min(0).max(6)).min(1, 'Choose at least one working day.'),
    open: time,
    close: time,
    // Schedules saved before breaks existed get the lunch break.
    breaks: z
      .array(z.object({ start: time, end: time }))
      .max(3)
      .default(DEFAULT_SCHEDULE.breaks),
    holidays: z
      .array(
        z.object({
          date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-12-25.'),
          name: z.string().trim().min(1, 'Name the holiday.').max(60),
        }),
      )
      .max(200),
  })
  .refine((s) => s.close > s.open, { message: 'Closing time must be after opening time.', path: ['close'] })
  .refine((s) => s.breaks.every((b) => b.start > s.open && b.end < s.close && b.end > b.start), {
    message: 'A break must start and end within office hours.',
    path: ['breaks'],
  });

const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const dayKey = (d: Date) => d.toISOString().slice(0, 10);
const startOfDay = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const DAY = 86_400_000;

/** Is `d`'s date a working day (right weekday, not a holiday)? */
export function isWorkingDay(s: OfficeSchedule, d: Date): boolean {
  return s.days.includes(d.getUTCDay()) && !s.holidays.some((h) => h.date === dayKey(d));
}

export function holidayOn(s: OfficeSchedule, d: Date): Holiday | undefined {
  return s.holidays.find((h) => h.date === dayKey(d));
}

/** Opening and closing time on `d`'s date, or null on a day off. */
function dayBounds(s: OfficeSchedule, d: Date): [Date, Date] | null {
  if (!isWorkingDay(s, d)) return null;
  const day = startOfDay(d).getTime();
  return [new Date(day + minutesOf(s.open) * 60_000), new Date(day + minutesOf(s.close) * 60_000)];
}

/** The staffed periods on `d`'s date: the office day with the breaks taken out. */
function periodsOn(s: OfficeSchedule, d: Date): [Date, Date][] {
  const bounds = dayBounds(s, d);
  if (!bounds) return [];
  const day = startOfDay(d).getTime();
  const at = (hhmm: string) => new Date(day + minutesOf(hhmm) * 60_000);
  const periods: [Date, Date][] = [];
  let start = bounds[0];
  for (const b of [...(s.breaks ?? [])].sort((x, y) => x.start.localeCompare(y.start))) {
    periods.push([start, at(b.start)]);
    start = at(b.end);
  }
  periods.push([start, bounds[1]]);
  return periods.filter(([a, b]) => b > a);
}

/** Between opening and closing time on a working day (breaks included). */
export function isWithinHours(s: OfficeSchedule, at: Date): boolean {
  const b = dayBounds(s, at);
  return !!b && at >= b[0] && at < b[1];
}

/** Staffed right now: within hours and not on a break. */
export function isOpenAt(s: OfficeSchedule, at: Date): boolean {
  return periodsOn(s, at).some(([a, b]) => at >= a && at < b);
}

/** During a break: when it ends. */
export function breakEndsAt(s: OfficeSchedule, at: Date): Date | null {
  return isWithinHours(s, at) && !isOpenAt(s, at) ? nextOpening(s, at) : null;
}

/** The next moment the office is staffed: `at` itself if it is now. */
export function nextOpening(s: OfficeSchedule, at: Date): Date {
  let day = startOfDay(at);
  // A year is plenty; a schedule with no working days is refused by the schema.
  for (let i = 0; i < 370; i++, day = new Date(day.getTime() + DAY)) {
    for (const [a, b] of periodsOn(s, day)) {
      if (at < a) return a;
      if (at < b) return at;
    }
  }
  return at;
}

/** When the office last closed before `at` (the end of the previous working day). */
export function previousClose(s: OfficeSchedule, at: Date): Date {
  let day = startOfDay(at);
  for (let i = 0; i < 370; i++, day = new Date(day.getTime() - DAY)) {
    const b = dayBounds(s, day);
    if (b && b[1] <= at) return b[1];
  }
  return new Date(at.getTime() - DAY);
}

/** `minutes` of working time after `start`. Starting outside hours counts from the next opening. */
export function addWorkingMinutes(s: OfficeSchedule, start: Date, minutes: number): Date {
  let left = minutes * 60_000;
  let at = nextOpening(s, start);
  for (let i = 0; i < 10_000 && left > 0; i++) {
    const period = periodsOn(s, at).find(([a, b]) => at >= a && at < b)!;
    const chunk = period[1].getTime() - at.getTime();
    if (left <= chunk) return new Date(at.getTime() + left);
    left -= chunk;
    at = nextOpening(s, period[1]);
  }
  return at;
}

/** Working minutes between two instants (0 if `to` is before `from`). */
export function workingMinutesBetween(s: OfficeSchedule, from: Date, to: Date): number {
  if (to <= from) return 0;
  let total = 0;
  let day = startOfDay(from);
  for (let i = 0; i < 3700 && day < to; i++, day = new Date(day.getTime() + DAY)) {
    for (const [a, b] of periodsOn(s, day)) {
      const start = Math.max(a.getTime(), from.getTime());
      const end = Math.min(b.getTime(), to.getTime());
      if (end > start) total += end - start;
    }
  }
  return Math.round(total / 60_000);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon–Fri, 08:00–17:00 (break 12:00–13:00)" */
export function describeSchedule(s: OfficeSchedule): string {
  const days = [...s.days].sort();
  const consecutive = days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
  const dayText =
    consecutive && days.length > 2
      ? `${WEEKDAYS[days[0]!]}–${WEEKDAYS[days.at(-1)!]}`
      : days.map((d) => WEEKDAYS[d]).join(', ');
  const breaks = (s.breaks ?? []).map((b) => `${b.start}–${b.end}`).join(', ');
  return `${dayText}, ${s.open}–${s.close}${breaks ? ` (break ${breaks})` : ''}`;
}
